import { APIComponentAttributes } from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { omit, pick } from 'lodash';
import type {
  InventoryComponentRow,
  InventoryDatasourceSelectionType,
} from './types';
import { TAGS_COLUMN } from './inventoryConsts';

/**
 * Properties that only exist on grid rows: the report and hierarchy endpoints
 * provide them, but they should not be sent back.
 */
const ROW_LOCAL_PROPERTIES = [
  'derivedData',
  'parentName',
  'completePath',
  'parentParent',
  'rootWorkspaceName',
  '_is_match',
  '_children',
  'childCount',
  'catalogLink',
];

/**
 * Row properties set from the component on read: the row's `entityType` is the
 * component type name, which the write APIs return as `type`.
 */
const DERIVED_ROW_PROPERTIES = ['entityType'];

/**
 * Maps component from report-specific format to API format.
 * This includes splitting date range fields.
 */
const mapReportComponentRowToApiComponentFormat = (
  component: InventoryComponentRow,
  scopeData: EnhancedScopeData,
  dataSourceType: InventoryDatasourceSelectionType
): APIComponentAttributes => {
  // Remove report specific properties that we do not want to store in the components
  const ignoredProperties = [
    ...ROW_LOCAL_PROPERTIES,
    ...DERIVED_ROW_PROPERTIES,
    'ardoq',
  ];
  const componentWithSkippedFields = omit(
    component,
    ignoredProperties
  ) as APIComponentAttributes;
  const componentWithSplitFields = dateRangeOperations.splitFieldsOnEntity(
    componentWithSkippedFields,
    component.rootWorkspace,
    scopeData
  );
  if (dataSourceType === 'report') {
    /**
     * Batch api does not deal well with partial updates to components when some fields are missing.
     * To avoid issues, we merge the existing component data from scopeData with the new data until the batch api supports partial updates better.
     */
    const componentFromScopeData = scopeData.componentsById[component._id];

    // Split date range fields in scopeData component to avoid sending both merged and split fields
    const componentFromScopeDataWithSplitFields = componentFromScopeData
      ? dateRangeOperations.splitFieldsOnEntity(
          omit(
            componentFromScopeData,
            ignoredProperties
          ) as APIComponentAttributes,
          component.rootWorkspace,
          scopeData
        )
      : {};

    return {
      ...componentFromScopeDataWithSplitFields,
      ...componentWithSplitFields,
    };
  }

  return componentWithSplitFields;
};

/**
 * Maps component from API format to the format that AG Grid expects.
 * This includes merging date range fields.
 */
const mapComponentFromApiComponentFormat = (
  component: APIComponentAttributes,
  scopeData: EnhancedScopeData,
  previousRowData?: InventoryComponentRow
): InventoryComponentRow => {
  const componentWithMergedDateRangeFields =
    dateRangeOperations.mergeDateRangeFieldsForEntity(
      component,
      scopeData.dateRangeFieldMap
    ) as APIComponentAttributes;
  const entityType = component.type;

  if (!previousRowData) {
    return {
      ...componentWithMergedDateRangeFields,
      entityType,
      derivedData: {},
    };
  }

  // The update APIs don't return these row-local fields, so fall back to the
  // existing row to avoid clearing them when an unrelated field is edited.
  return {
    ...pick(previousRowData, [...ROW_LOCAL_PROPERTIES, TAGS_COLUMN]),
    ...componentWithMergedDateRangeFields,
    entityType,
    derivedData: previousRowData.derivedData ?? {},
  };
};

export const reportComponentRowsOperations = {
  mapComponentFromApiComponentFormat,
  mapReportComponentRowToApiComponentFormat,
};
